import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ICertificate, IEnrollment } from '@stem/contracts';
import { apiClient } from '../services/api';
import { useSession } from '../store/session';
import { EmptyState } from '../dashboards/parts';

/** My certificates, and — for whoever may issue them — completions awaiting one. */
export function CertificatesPage() {
  const queryClient = useQueryClient();
  const canIssue = useSession(
    state => state.permissions.includes('certificate@issue') || state.permissions.includes('*@*'),
  );

  const certificates = useQuery({
    queryKey: ['my-certificates'],
    queryFn: () => apiClient.get<ICertificate[]>('/v1/certificates/mine'),
  });

  const enrollments = useQuery({
    queryKey: ['enrollments'],
    queryFn: () =>
      apiClient.get<Array<IEnrollment & { courseTitle: string }>>('/v1/learn/enrollments'),
  });

  const issue = useMutation({
    mutationFn: (enrollmentId: string) =>
      apiClient.post<ICertificate>('/v1/certificates', { enrollmentId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['my-certificates'] }),
  });

  const held = new Set((certificates.data ?? []).map(row => row.courseId));
  const eligible = (enrollments.data ?? []).filter(
    row => row.status === 'completed' && !held.has(row.courseId),
  );

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink-900">Certificates</h1>
      <p className="text-sm text-ink-600">
        Each certificate carries a serial that anyone can check without signing in.
      </p>

      {canIssue && eligible.length > 0 && (
        <section className="card mt-5">
          <h2 className="text-sm font-semibold text-ink-900">Ready to issue</h2>
          <ul className="mt-2 divide-y divide-ink-100">
            {eligible.map(row => (
              <li key={row.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{row.courseTitle}</span>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={issue.isPending}
                  onClick={() => issue.mutate(row.id)}
                >
                  Issue certificate
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {(certificates.data ?? []).map(certificate => (
          <article key={certificate.id} className="card">
            <p className="text-xs uppercase tracking-wide text-ink-500">
              {new Date(certificate.issuedAt).toLocaleDateString()}
            </p>
            <h2 className="mt-1 text-base font-semibold text-ink-900">{certificate.courseTitle}</h2>
            <p className="text-sm text-ink-600">{certificate.recipientName}</p>

            <p className="mt-3 font-mono text-sm tracking-wider text-brand-900">
              {certificate.serial}
            </p>

            {certificate.revokedAt ? (
              <p className="mt-1 text-xs text-red-700">
                Revoked {new Date(certificate.revokedAt).toLocaleDateString()}
              </p>
            ) : (
              <a
                className="mt-2 inline-block text-xs text-brand-700 underline"
                href={`/verify/${certificate.serial}`}
                target="_blank"
                rel="noreferrer"
              >
                Public verification page
              </a>
            )}
          </article>
        ))}
      </div>

      {certificates.data && certificates.data.length === 0 && (
        <EmptyState>
          No certificates yet. Complete a course and one can be issued for it.
        </EmptyState>
      )}
    </div>
  );
}
